// =============================================================================
// Room name normalization and fuzzy matching (mirrors SQL normalize_room_name).
// Used for "Did you mean Kitchen?" and dedup.
// =============================================================================

/**
 * Normalize room name: trim, lowercase, collapse spaces, remove punctuation.
 * Must match DB function normalize_room_name() for consistency.
 */
export function normalizeRoomName(name: string): string {
  if (name == null || typeof name !== "string") return "";
  return name
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Simple similarity: 0 = no match, 1 = exact.
 * Uses normalized strings; good for "Kichen" vs "Kitchen".
 */
function similarity(a: string, b: string): number {
  const na = normalizeRoomName(a);
  const nb = normalizeRoomName(b);
  if (na === nb) return 1;
  if (na.length === 0 || nb.length === 0) return 0;
  // One contains the other
  if (na.includes(nb) || nb.includes(na)) return 0.8;
  // Levenshtein-like: count matching chars (order-sensitive)
  let matches = 0;
  let i = 0;
  let j = 0;
  while (i < na.length && j < nb.length) {
    if (na[i] === nb[j]) {
      matches++;
      i++;
      j++;
    } else {
      if (na.length < nb.length) j++;
      else i++;
    }
  }
  const maxLen = Math.max(na.length, nb.length);
  return maxLen === 0 ? 0 : matches / maxLen;
}

export interface MatchRoomResult {
  /** Exact match by name_normalized */
  exact: { id: string; name: string } | null;
  /** Close match for "Did you mean?" (e.g. similarity >= 0.6) */
  suggested: { id: string; name: string } | null;
  /** Normalized input for creating new room */
  nameNormalized: string;
}

/**
 * Match input name against existing property rooms.
 * Returns exact match, optional suggested match for "Did you mean?", and normalized name.
 */
export function matchRoom(
  inputName: string,
  existingRooms: { id: string; name: string }[]
): MatchRoomResult {
  const nameNormalized = normalizeRoomName(inputName);
  const trimmed = (inputName ?? "").trim();

  let exact: { id: string; name: string } | null = null;
  let bestSuggested: { id: string; name: string; score: number } | null = null;
  const threshold = 0.6;

  for (const r of existingRooms) {
    const rNorm = normalizeRoomName(r.name);
    if (rNorm === nameNormalized) {
      exact = { id: r.id, name: r.name };
      break;
    }
    const score = similarity(trimmed, r.name);
    if (score >= threshold && (!bestSuggested || score > bestSuggested.score)) {
      bestSuggested = { id: r.id, name: r.name, score };
    }
  }

  return {
    exact,
    suggested: bestSuggested ? { id: bestSuggested.id, name: bestSuggested.name } : null,
    nameNormalized: nameNormalized || trimmed.toLowerCase(),
  };
}

const NEAR_DUP_THRESHOLD = 0.6;

/**
 * Find pairs of rooms that look like duplicates (similarity >= threshold).
 * Returns list of { source, target } where target is the one to keep (e.g. first alphabetically by name).
 */
export function findNearDuplicatePairs(
  rooms: { id: string; name: string }[]
): { source: { id: string; name: string }; target: { id: string; name: string } }[] {
  const pairs: { source: { id: string; name: string }; target: { id: string; name: string } }[] = [];
  const used = new Set<string>();
  for (let i = 0; i < rooms.length; i++) {
    if (used.has(rooms[i].id)) continue;
    for (let j = i + 1; j < rooms.length; j++) {
      if (used.has(rooms[j].id)) continue;
      const sim = similarity(rooms[i].name, rooms[j].name);
      if (sim >= NEAR_DUP_THRESHOLD && sim < 1) {
        const [source, target] = rooms[i].name.localeCompare(rooms[j].name) <= 0 ? [rooms[j], rooms[i]] : [rooms[i], rooms[j]];
        pairs.push({ source, target });
        used.add(source.id);
        break;
      }
    }
  }
  return pairs;
}
